import api from '../../utils/api';
import type {
  PushDeliveryLog,
  PushDeliveryStatusCounts,
  PushNotificationTemplate,
  PushPaged,
  PushRecipientRow,
} from '../../models/MasterAdmin/pushNotificationHub';

const BASE = '/master-admin/notifications/campaigns';
const FCM_BASE = '/master-admin/notifications/fcm-tokens';

function appendFields(fd: FormData, values: Record<string, string | undefined>) {
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
}

export type PushTemplateFormPayload = {
  name: string;
  title: string;
  message: string;
  notificationType: string;
  intentType: string;
  intentValue?: string;
  isActive?: boolean;
  image?: File | null;
  bannerImage?: File | null;
  clearImage?: boolean;
  clearBannerImage?: boolean;
};

export async function fetchPushTemplates(params?: {
  search?: string;
  activeOnly?: boolean;
}): Promise<{ success: boolean; data: { items: PushNotificationTemplate[] } }> {
  const response = await api.get(`${BASE}/templates`, {
    params: {
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.activeOnly ? { activeOnly: true } : {}),
    },
  });
  return response.data;
}

export async function createPushTemplate(
  form: PushTemplateFormPayload
): Promise<{ success: boolean; data: PushNotificationTemplate }> {
  const fd = new FormData();
  appendFields(fd, {
    name: form.name,
    title: form.title,
    message: form.message,
    notificationType: form.notificationType,
    intentType: form.intentType,
    intentValue: form.intentValue || '',
    isActive: String(form.isActive !== false),
  });
  if (form.image) fd.append('image', form.image);
  if (form.bannerImage) fd.append('bannerImage', form.bannerImage);
  const response = await api.post(`${BASE}/templates`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function updatePushTemplate(
  id: string,
  form: PushTemplateFormPayload
): Promise<{ success: boolean; data: PushNotificationTemplate }> {
  const fd = new FormData();
  appendFields(fd, {
    name: form.name,
    title: form.title,
    message: form.message,
    notificationType: form.notificationType,
    intentType: form.intentType,
    intentValue: form.intentValue || '',
    isActive: String(form.isActive !== false),
  });
  if (form.clearImage) fd.append('clearImage', 'true');
  if (form.clearBannerImage) fd.append('clearBannerImage', 'true');
  if (form.image) fd.append('image', form.image);
  if (form.bannerImage) fd.append('bannerImage', form.bannerImage);
  const response = await api.put(`${BASE}/templates/${encodeURIComponent(id)}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function deletePushTemplate(id: string): Promise<void> {
  await api.delete(`${BASE}/templates/${encodeURIComponent(id)}`);
}

export async function fetchPushDeliveries(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  campaignId?: string;
  userId?: string;
  recipientType?: string;
  days?: number;
}): Promise<{ success: boolean; data: PushPaged<PushDeliveryLog> }> {
  const response = await api.get(`${BASE}/deliveries`, { params });
  return response.data;
}

export async function fetchPushDeliveryStats(params?: {
  search?: string;
  campaignId?: string;
  days?: number;
  recipientType?: string;
}): Promise<{ success: boolean; data: PushDeliveryStatusCounts }> {
  const response = await api.get(`${BASE}/deliveries/stats`, { params });
  return response.data;
}

export type PushAnalyticsOverview = {
  periodDays: number;
  summary: PushDeliveryStatusCounts & {
    deliveryRatePct?: number;
    readRatePct?: number;
    failRatePct?: number;
  };
  dailyTrend: { date: string; sent: number; failed: number; read: number; total: number }[];
  byPlatform: { platform: string; sent: number; failed: number; read: number; total: number }[];
  byRecipientType: { recipientType: string; sent: number; failed: number; total: number }[];
  byType: {
    notificationType: string;
    label: string;
    delivered: number;
    failed: number;
    campaignCount: number;
    successPct: number;
  }[];
  topErrors: { code: string; label: string; count: number; hint: string }[];
  topCampaigns: {
    id: string;
    title: string;
    notificationType: string;
    status: string;
    delivered: number;
    failed: number;
    sendAttempts: number;
    successPct: number;
    lastAt: string | null;
  }[];
  insights: { severity: 'info' | 'warn' | 'good'; title: string; detail: string }[];
};

export async function fetchPushAnalyticsOverview(params?: {
  days?: number;
}): Promise<{ success: boolean; data: PushAnalyticsOverview }> {
  const response = await api.get(`${BASE}/analytics/overview`, { params });
  return response.data;
}

export async function fetchPushRecipients(params?: {
  page?: number;
  limit?: number;
  search?: string;
  recipientType?: string;
  days?: number;
}): Promise<{ success: boolean; data: PushPaged<PushRecipientRow> }> {
  const response = await api.get(`${BASE}/recipients`, { params });
  return response.data;
}

export async function fetchPushRecipientDeliveries(
  userId: string,
  params?: { page?: number; limit?: number; recipientType?: string; vendorId?: string }
): Promise<{ success: boolean; data: PushPaged<PushDeliveryLog> }> {
  const response = await api.get(`${BASE}/recipients/${encodeURIComponent(userId)}/deliveries`, { params });
  return response.data;
}

export async function fetchFcmTokenInsights(): Promise<{
  success: boolean;
  data: import('../../models/MasterAdmin/pushNotificationHub').FcmTokenInsights;
}> {
  const response = await api.get(`${FCM_BASE}/insights`);
  return response.data;
}

export async function fetchFcmOwners(params?: {
  page?: number;
  limit?: number;
  search?: string;
  ownerType?: string;
  platform?: string;
  activeOnly?: boolean;
  notifiableOnly?: boolean;
  days?: number;
}): Promise<{
  success: boolean;
  data: PushPaged<import('../../models/MasterAdmin/pushNotificationHub').FcmTokenOwnerRow>;
}> {
  const response = await api.get(FCM_BASE, {
    params: {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      ownerType: params?.ownerType,
      platform: params?.platform,
      days: params?.days,
      ...(params?.activeOnly ? { activeOnly: '1' } : {}),
      ...(params?.notifiableOnly ? { notifiableOnly: '1' } : {}),
    },
  });
  return response.data;
}

export async function fetchOwnerFcmTokens(
  ownerType: string,
  ownerId: string
): Promise<{
  success: boolean;
  data: { tokens: import('../../models/MasterAdmin/pushNotificationHub').FcmTokenDeviceRow[] };
}> {
  const response = await api.get(
    `${FCM_BASE}/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}`
  );
  return response.data;
}

export async function validateOwnerFcmTokens(
  ownerType: string,
  ownerId: string,
  deactivateInvalid = false
): Promise<{
  success: boolean;
  data: {
    validCount: number;
    invalidCount: number;
    deactivated: boolean;
    tokens: import('../../models/MasterAdmin/pushNotificationHub').FcmTokenDeviceRow[];
  };
}> {
  const response = await api.post(
    `${FCM_BASE}/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/validate`,
    { deactivateInvalid }
  );
  return response.data;
}
